import { useCallback } from "react";
import { usePessoaFisicaApi } from "../../../infra/geo-veles";
import { useUIService } from "../../ui";
import { useAppDispatch } from "../../../presentation/hooks/store-hooks";
import {
  setCidadeUFCountState,
  setCidadeUFLoading,
  setCidadeUFError, 
} from "../../../application/states/geo-veles";
import { ICidadeUFRequest } from "../../../application/models";
import { NormalizarNomeCidade } from "../../../utils/formatters/cidade";

export const useCidadeUFService = () => {
  // Dispatch
  const dispatch = useAppDispatch();
  // Infra
  const { findCidadeUFCountApi } = usePessoaFisicaApi();
  // Hook
  const { setAlert } = useUIService();

  // Função auxiliar para normalizar nomes (mesmo padrão da getEstadoValue)
 const NormalizarNomes = (nome: string): string => {
    if (!nome) return "";

    let resultado = nome.trim();

    // 1. Remove acentos (converte caracteres acentuados para seus equivalentes sem acento)
    resultado = resultado.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    // 2. Remove caracteres especiais (mantém apenas letras, números e espaços)
    resultado = resultado.replace(/[^a-zA-Z0-9\s]/g, " ");

    // 3. Remove espaços extras e converte para maiúsculo
    resultado = resultado.trim().replace(/\s+/g, " ").toUpperCase();

    return resultado;
};

 const findCidadeUFCount = useCallback(async (request: ICidadeUFRequest) => {
    try {
        dispatch(setCidadeUFLoading(true))
        
        // Normaliza os dados antes de enviar para a API
        const requestNormalizado: ICidadeUFRequest = {
            cidadesUfs: request.cidadesUfs.map(item => ({
                cidade: NormalizarNomeCidade(item.cidade),
                uf: NormalizarNomes(item.uf)
            }))
        }

        const getCidadeUFCount = await findCidadeUFCountApi(requestNormalizado)

        // Verificar se getCidadeUFCount é um array
        let cidadesUfsArray = getCidadeUFCount;
  
        if (!Array.isArray(getCidadeUFCount)) {
            cidadesUfsArray = (getCidadeUFCount as any)?.data || (getCidadeUFCount as any)?.cidades || (getCidadeUFCount as any)?.result || [];
        }

        if (!Array.isArray(cidadesUfsArray)) {
            cidadesUfsArray = [];
        }

        // Cidades enviadas (normalizadas)
        const cidadesUfsEnviadas = requestNormalizado.cidadesUfs.map(item => ({
            cidade: item.cidade.toUpperCase().trim(),
            uf: item.uf.toUpperCase().trim()
        }))

        // Cidades retornadas pela API (válidas)
        const cidadesUfsRetornadas = cidadesUfsArray.map(item => ({
            cidade: item.cidade?.toUpperCase().trim() || '',
            uf: item.uf?.toUpperCase().trim() || ''
        }))

        // Encontrar cidades INVÁLIDAS (enviadas mas NÃO retornadas pela API)
        const cidadesUfsInvalidas = cidadesUfsEnviadas.filter(enviada => {
            const encontrada = cidadesUfsRetornadas.some(retornada => 
                retornada.cidade === enviada.cidade && retornada.uf === enviada.uf
            );
            return !encontrada;
        });

        // Encontrar cidades VÁLIDAS (retornadas pela API)
        const cidadesUfsValidas = cidadesUfsEnviadas.filter(enviada => {
            const encontrada = cidadesUfsRetornadas.some(retornada => 
                retornada.cidade === enviada.cidade && retornada.uf === enviada.uf
            );
            return encontrada;
        });

        // Formatar as listas para o formato "CIDADE/UF"
        const cidadesValidasFormatadas = cidadesUfsValidas.map(item => `${item.cidade}/${item.uf}`);
        const cidadesInvalidasFormatadas = cidadesUfsInvalidas.map(item => `${item.cidade}/${item.uf}`);

        // Se houver cidades inválidas, mostrar alerta específico
        if (cidadesInvalidasFormatadas.length > 0) {
            const cidadesInvalidasTexto = cidadesInvalidasFormatadas.join(', ')
            
            setAlert({
                message: `As seguintes Cidades/UF são inválidas (não encontradas no sistema): ${cidadesInvalidasTexto}`,
                type: 'warning'
            })
        }

        // Mostrar resumo da análise
        const totalEnviadas = cidadesUfsEnviadas.length
        const totalValidas = cidadesValidasFormatadas.length
        const totalInvalidas = cidadesInvalidasFormatadas.length

        if (totalEnviadas > 0) {
            setAlert({
                message: `Análise concluída: ${totalValidas} combinações válidas encontradas, ${totalInvalidas} inválidas de um total de ${totalEnviadas} enviadas.`,
                type: 'info'
            })
        }
        dispatch(setCidadeUFCountState({
            cidadeUFCount: {
                cidadesUfs: cidadesUfsArray,        // dados completos da API
                cidadesValidas: cidadesValidasFormatadas,  // apenas as válidas
                cidadesInvalidas: cidadesInvalidasFormatadas // apenas as inválidas
            }
        }))
        
        dispatch(setCidadeUFLoading(false))        
        return {
            validas: cidadesValidasFormatadas,
            invalidas: cidadesInvalidasFormatadas,
            todas: requestNormalizado.cidadesUfs.length
        };
        
    } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar dados de cidade e UF'
        
        dispatch(setCidadeUFError(errorMessage))
        dispatch(setCidadeUFLoading(false))
        
        setAlert({
            message: errorMessage,
            type: 'error'
        })
        
        throw error;
    }
}, [dispatch, findCidadeUFCountApi, setAlert])

  return {
    findCidadeUFCount,
  };
};
